/**
 * Cat Scholes puzzle (spec §6.14.4): Sokoban rules. The player pushes Cat Scholes and props onto
 * the green target tiles; a level is solved when every target is covered. The client plays with
 * these rules and the server replays the submitted moves with the same code, so a score is only
 * stored for a run that actually solves the level.
 *
 * Level rows use one character per tile:
 * `#` wall, ` ` floor, `.` target, `@` player, `+` player on a target, `C` the cat,
 * `c` the cat on a target, `$` a prop, `*` a prop on a target, `-` outside the room (not drawn).
 */

export type PuzzleMove = 'u' | 'd' | 'l' | 'r';

export const PUZZLE_MOVES: readonly PuzzleMove[] = ['u', 'd', 'l', 'r'];

/** Longest run a client may submit; far above any level's par. */
export const MAX_PUZZLE_MOVES = 1_000;

export interface PuzzleLevelSource {
  readonly id: string;
  readonly name: { readonly en: string; readonly sv: string };
  readonly rows: readonly string[];
  /** Fewest moves that solve the level (checked by a solver in the tests). */
  readonly par: number;
}

export type PuzzleTile = 'wall' | 'floor' | 'target' | 'void';

/** A level parsed into tiles plus the starting positions (cell index = y * width + x). */
export interface PuzzleLevel {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly tiles: readonly PuzzleTile[];
  readonly start: PuzzleState;
}

/** Where the movable things are. Props are kept sorted so equal positions compare equal. */
export interface PuzzleState {
  readonly player: number;
  readonly cat: number;
  readonly props: readonly number[];
}

const DELTAS: Readonly<Record<PuzzleMove, readonly [number, number]>> = {
  u: [0, -1],
  d: [0, 1],
  l: [-1, 0],
  r: [1, 0],
};

export function isPuzzleMove(value: string): value is PuzzleMove {
  return value === 'u' || value === 'd' || value === 'l' || value === 'r';
}

/** Parses level rows; throws when the level is malformed (unknown character, counts off). */
export function parsePuzzleLevel(source: Pick<PuzzleLevelSource, 'id' | 'rows'>): PuzzleLevel {
  const height = source.rows.length;
  const width = Math.max(0, ...source.rows.map((row) => row.length));
  if (width === 0 || height === 0) throw new Error(`Level ${source.id} is empty`);
  const tiles: PuzzleTile[] = [];
  const players: number[] = [];
  const cats: number[] = [];
  const props: number[] = [];
  source.rows.forEach((row, y) => {
    for (let x = 0; x < width; x += 1) {
      const char = row[x] ?? '-';
      const cell = y * width + x;
      switch (char) {
        case '#':
          tiles.push('wall');
          break;
        case '-':
          tiles.push('void');
          break;
        case ' ':
        case '@':
        case 'C':
        case '$':
          tiles.push('floor');
          break;
        case '.':
        case '+':
        case 'c':
        case '*':
          tiles.push('target');
          break;
        default:
          throw new Error(`Level ${source.id}: unknown tile "${char}" at ${x},${y}`);
      }
      if (char === '@' || char === '+') players.push(cell);
      if (char === 'C' || char === 'c') cats.push(cell);
      if (char === '$' || char === '*') props.push(cell);
    }
  });
  const [player] = players;
  const [cat] = cats;
  if (players.length !== 1 || player === undefined) {
    throw new Error(`Level ${source.id} needs exactly one player`);
  }
  if (cats.length !== 1 || cat === undefined) {
    throw new Error(`Level ${source.id} needs exactly one cat`);
  }
  const targets = tiles.filter((tile) => tile === 'target').length;
  if (targets !== props.length + 1) {
    throw new Error(`Level ${source.id} needs one target per prop plus one for the cat`);
  }
  return {
    id: source.id,
    width,
    height,
    tiles,
    start: { player, cat, props: [...props].sort((a, b) => a - b) },
  };
}

/** The state after one move, or `undefined` when the move is blocked. */
export function applyPuzzleMove(
  level: PuzzleLevel,
  state: PuzzleState,
  move: PuzzleMove,
): PuzzleState | undefined {
  const next = neighbour(level, state.player, move);
  if (next === undefined || !isOpen(level, next)) return undefined;
  const pushesCat = next === state.cat;
  const pushedProp = state.props.indexOf(next);
  if (!pushesCat && pushedProp < 0) return { ...state, player: next };

  const beyond = neighbour(level, next, move);
  if (beyond === undefined || !isOpen(level, beyond)) return undefined;
  if (beyond === state.cat || state.props.includes(beyond)) return undefined;
  if (pushesCat) return { ...state, player: next, cat: beyond };
  const props = state.props.map((cell, index) => (index === pushedProp ? beyond : cell));
  return { ...state, player: next, props: props.sort((a, b) => a - b) };
}

/** True when the cat and every prop stand on targets (there are exactly as many targets). */
export function isPuzzleSolved(level: PuzzleLevel, state: PuzzleState): boolean {
  return (
    level.tiles[state.cat] === 'target' &&
    state.props.every((cell) => level.tiles[cell] === 'target')
  );
}

export type PuzzleReplay =
  | { readonly ok: true; readonly solved: boolean; readonly moves: number }
  | {
      readonly ok: false;
      readonly reason: 'invalid-move' | 'blocked' | 'after-solved' | 'too-long';
      readonly at: number;
    };

/**
 * Replays a run (`'rrud…'`) from the level's start. Every move must be legal and nothing may
 * follow the move that solves the level, so the move count is exactly the run's length.
 */
export function replayPuzzle(level: PuzzleLevel, moves: string): PuzzleReplay {
  if (moves.length > MAX_PUZZLE_MOVES)
    return { ok: false, reason: 'too-long', at: MAX_PUZZLE_MOVES };
  let state = level.start;
  let solved = isPuzzleSolved(level, state);
  for (let i = 0; i < moves.length; i += 1) {
    if (solved) return { ok: false, reason: 'after-solved', at: i };
    const move = moves[i] ?? '';
    if (!isPuzzleMove(move)) return { ok: false, reason: 'invalid-move', at: i };
    const next = applyPuzzleMove(level, state, move);
    if (!next) return { ok: false, reason: 'blocked', at: i };
    state = next;
    solved = isPuzzleSolved(level, state);
  }
  return { ok: true, solved, moves: moves.length };
}

/**
 * Fewest moves that solve the level (breadth-first search over positions), or `undefined` when it
 * cannot be solved. Meant for tests and level design; the levels are small enough for this.
 */
export function solvePuzzle(level: PuzzleLevel, maxStates = 500_000): string | undefined {
  const key = (s: PuzzleState) => `${s.player}|${s.cat}|${s.props.join(',')}`;
  const seen = new Map<string, string>([[key(level.start), '']]);
  const queue: PuzzleState[] = [level.start];
  // The queue grows while it is walked; for-of visits the appended states too.
  for (const state of queue) {
    const path = seen.get(key(state)) ?? '';
    if (isPuzzleSolved(level, state)) return path;
    for (const move of PUZZLE_MOVES) {
      const next = applyPuzzleMove(level, state, move);
      if (!next) continue;
      const k = key(next);
      if (seen.has(k)) continue;
      if (seen.size >= maxStates) return undefined;
      seen.set(k, path + move);
      queue.push(next);
    }
  }
  return undefined;
}

/** Puzzle milestones: each adds a gold kintsugi crack to Cat Scholes (spec §6.14.4). */
export const CAT_CRACK_MILESTONES = [3, 6, 9] as const;

/** Number of cracks (0–3) earned by solving `solved` different levels. */
export function catCracks(solved: number): number {
  return CAT_CRACK_MILESTONES.filter((milestone) => solved >= milestone).length;
}

function neighbour(level: PuzzleLevel, cell: number, move: PuzzleMove): number | undefined {
  const [dx, dy] = DELTAS[move];
  const x = (cell % level.width) + dx;
  const y = Math.floor(cell / level.width) + dy;
  if (x < 0 || y < 0 || x >= level.width || y >= level.height) return undefined;
  return y * level.width + x;
}

function isOpen(level: PuzzleLevel, cell: number): boolean {
  const tile = level.tiles[cell];
  return tile === 'floor' || tile === 'target';
}
